// pages/admin/AdminPrinters.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  TablePagination,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Fade,
  Grow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Computer as ComputerIcon,
  Print as PrintServiceIcon,
  Settings as SettingsIcon,
  NetworkCheck as NetworkCheckIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const AdminPrinters = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [printerToDelete, setPrinterToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [testPrinting, setTestPrinting] = useState(false);
  const [statistics, setStatistics] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    printer_type: 'kitchen',
    ip_address: '',
    port: 9100,
    location: '',
    is_active: true,
    is_default: false
  });

  const printerTypes = [
    { value: 'kitchen', label: 'Kitchen', label_ar: 'المطبخ' },
    { value: 'bar', label: 'Bar', label_ar: 'البار' },
    { value: 'drinks', label: 'Drinks', label_ar: 'المشروبات' },
    { value: 'dessert', label: 'Dessert', label_ar: 'الحلويات' },
    { value: 'receipt', label: 'Receipt', label_ar: 'الإيصالات' },
    { value: 'coffee', label: 'Coffee', label_ar: 'القهوة' },
    { value: 'grill', label: 'Grill', label_ar: 'الشواية' },
    { value: 'salad', label: 'Salad', label_ar: 'السلطات' }
  ];

  useEffect(() => {
    fetchPrinters();
    fetchStatistics();
  }, []);

  const fetchPrinters = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/printers');
      setPrinters(response.data.printers || []);
    } catch (error) {
      console.error('Error fetching printers:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في تحميل الطابعات' : 'Failed to load printers',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/admin/printers/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleOpenDialog = (printer = null) => {
    if (printer) {
      setEditingPrinter(printer);
      setFormData({
        name: printer.name,
        name_ar: printer.name_ar || '',
        printer_type: printer.printer_type,
        ip_address: printer.ip_address,
        port: printer.port,
        location: printer.location || '',
        is_active: printer.is_active === 1,
        is_default: printer.is_default === 1
      });
    } else {
      setEditingPrinter(null);
      setFormData({
        name: '',
        name_ar: '',
        printer_type: 'kitchen',
        ip_address: '',
        port: 9100,
        location: '',
        is_active: true,
        is_default: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPrinter(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' || name === 'is_default' ? checked : value
    }));
  };

  const handleSavePrinter = async () => {
    try {
      if (!formData.name || !formData.ip_address) {
        showSnackbar(
          language === 'ar' ? 'الاسم وعنوان IP مطلوبان' : 'Name and IP address are required',
          'error'
        );
        return;
      }

      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      if (!ipRegex.test(formData.ip_address)) {
        showSnackbar(
          language === 'ar' ? 'عنوان IP غير صالح' : 'Invalid IP address',
          'error'
        );
        return;
      }

      if (editingPrinter) {
        await api.put(`/admin/printers/${editingPrinter.id}`, formData);
        showSnackbar(
          language === 'ar' ? 'تم تحديث الطابعة بنجاح' : 'Printer updated successfully',
          'success'
        );
      } else {
        await api.post('/admin/printers', formData);
        showSnackbar(
          language === 'ar' ? 'تم إضافة الطابعة بنجاح' : 'Printer added successfully',
          'success'
        );
      }
      
      handleCloseDialog();
      fetchPrinters();
      fetchStatistics();
    } catch (error) {
      console.error('Error saving printer:', error);
      showSnackbar(
        error.response?.data?.message || (language === 'ar' ? 'فشل في حفظ الطابعة' : 'Failed to save printer'),
        'error'
      );
    }
  };

  const handleDeleteClick = (printer) => {
    setPrinterToDelete(printer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!printerToDelete) return;
    
    try {
      await api.delete(`/admin/printers/${printerToDelete.id}`);
      showSnackbar(
        language === 'ar' ? 'تم حذف الطابعة بنجاح' : 'Printer deleted successfully',
        'success'
      );
      setDeleteDialogOpen(false);
      setPrinterToDelete(null);
      fetchPrinters();
      fetchStatistics();
    } catch (error) {
      console.error('Error deleting printer:', error);
      showSnackbar(
        error.response?.data?.message || (language === 'ar' ? 'فشل في حذف الطابعة' : 'Failed to delete printer'),
        'error'
      );
    }
  };

  const handleTestPrint = async (printer) => {
    setTestPrinting(true);
    try {
      const response = await api.post(`/admin/printers/${printer.id}/test`);
      showSnackbar(
        language === 'ar' ? `تم إرسال أمر الطباعة إلى ${printer.name}` : `Test print sent to ${printer.name}`,
        'success'
      );
    } catch (error) {
      console.error('Error testing printer:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في اختبار الطابعة' : 'Failed to test printer',
        'error'
      );
    } finally {
      setTestPrinting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (isActive) => {
    if (isActive) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label={language === 'ar' ? 'نشط' : 'Active'}
          color="success"
          size="small"
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon />}
        label={language === 'ar' ? 'غير نشط' : 'Inactive'}
        color="error"
        size="small"
      />
    );
  };

  const getPrinterTypeLabel = (type) => {
    const found = printerTypes.find(t => t.value === type);
    if (!found) return type;
    return language === 'ar' ? found.label_ar : found.label;
  };

  const paginatedPrinters = printers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Mobile card view for printers
  const PrinterCard = ({ printer }) => (
    <Card sx={{
      backgroundColor: '#1A1A1A',
      border: '1px solid rgba(255, 215, 0, 0.2)',
      borderRadius: '16px',
      mb: 2,
      '&:hover': {
        borderColor: '#FFD700',
        transform: 'translateY(-2px)',
        transition: 'all 0.2s ease'
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ComputerIcon sx={{ color: '#FFD700' }} />
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {language === 'ar' ? printer.name_ar || printer.name : printer.name}
            </Typography>
            {printer.is_default && (
              <Chip label={language === 'ar' ? 'افتراضي' : 'Default'} size="small" sx={{ backgroundColor: '#4CAF50', color: '#FFF', height: 20 }} />
            )}
          </Box>
          {getStatusChip(printer.is_active)}
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'النوع' : 'Type'}</Typography>
            <Chip label={getPrinterTypeLabel(printer.printer_type)} size="small" sx={{ backgroundColor: '#FFD700', color: '#000', mt: 0.5 }} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>IP Address</Typography>
            <Typography variant="body2" sx={{ color: '#FFF', fontFamily: 'monospace' }}>{printer.ip_address}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>Port</Typography>
            <Typography variant="body2" sx={{ color: '#FFF' }}>{printer.port}</Typography>
          </Box>
          {printer.location && (
            <Box>
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الموقع' : 'Location'}</Typography>
              <Typography variant="body2" sx={{ color: '#FFF' }}>{printer.location}</Typography>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Tooltip title={language === 'ar' ? 'اختبار الطباعة' : 'Test Print'}>
            <IconButton onClick={() => handleTestPrint(printer)} disabled={testPrinting} sx={{ color: '#FFD700', backgroundColor: 'rgba(255, 215, 0, 0.1)', '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.2)' } }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={language === 'ar' ? 'تعديل' : 'Edit'}>
            <IconButton onClick={() => handleOpenDialog(printer)} sx={{ color: '#FFD700', backgroundColor: 'rgba(255, 215, 0, 0.1)', '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.2)' } }}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={language === 'ar' ? 'حذف' : 'Delete'}>
            <IconButton onClick={() => handleDeleteClick(printer)} sx={{ color: '#FF3D00', backgroundColor: 'rgba(255, 61, 0, 0.1)', '&:hover': { backgroundColor: 'rgba(255, 61, 0, 0.2)' } }}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, backgroundColor: '#000000', minHeight: '100vh' }}>
      {/* Header with Navigation Button */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 4, 
        gap: 2 
      }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}>
            {language === 'ar' ? 'إدارة الطابعات' : 'Printer Management'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#B3B3B3', mt: 0.5 }}>
            {language === 'ar' ? 'إدارة طابعات المطبخ والإيصالات' : 'Manage kitchen and receipt printers'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          {/* Print Service Client Navigation Button */}
          <Button
            variant="outlined"
            startIcon={<PrintServiceIcon />}
            onClick={() => navigate('/admin/adminprinters/client')}
            sx={{
              borderColor: '#FFD700',
              color: '#FFD700',
              fontWeight: 700,
              px: 3,
              py: { xs: 1, sm: 1.2 },
              '&:hover': {
                borderColor: '#FFE55C',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease'
            }}
          >
            {language === 'ar' ? 'خدمة طباعة POS' : 'POS Print Service'}
          </Button>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 700,
              px: 3,
              py: { xs: 1, sm: 1.2 },
              '&:hover': { 
                backgroundColor: '#FFE55C',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease'
            }}
          >
            {language === 'ar' ? 'إضافة طابعة' : 'Add Printer'}
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards - Modern Grid */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Fade in timeout={500}>
              <Paper sx={{ 
                p: { xs: 2, sm: 3 }, 
                backgroundColor: '#0D0D0D', 
                border: '1px solid #2D2D2D', 
                borderRadius: '16px',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                  transform: 'translateY(-4px)'
                }
              }}>
                <PrintIcon sx={{ color: '#FFD700', fontSize: { xs: 32, sm: 40 }, mb: 1 }} />
                <Typography variant="h3" sx={{ color: '#FFD700', fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {statistics.total_printers}
                </Typography>
                <Typography sx={{ color: '#B3B3B3', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'إجمالي الطابعات' : 'Total Printers'}
                </Typography>
              </Paper>
            </Fade>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Fade in timeout={700}>
              <Paper sx={{ 
                p: { xs: 2, sm: 3 }, 
                backgroundColor: '#0D0D0D', 
                border: '1px solid #2D2D2D', 
                borderRadius: '16px',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                  transform: 'translateY(-4px)'
                }
              }}>
                <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: { xs: 32, sm: 40 }, mb: 1 }} />
                <Typography variant="h3" sx={{ color: '#4CAF50', fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {statistics.active_printers}
                </Typography>
                <Typography sx={{ color: '#B3B3B3', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'طابعات نشطة' : 'Active Printers'}
                </Typography>
              </Paper>
            </Fade>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Fade in timeout={900}>
              <Paper sx={{ 
                p: { xs: 2, sm: 3 }, 
                backgroundColor: '#0D0D0D', 
                border: '1px solid #2D2D2D', 
                borderRadius: '16px',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                  transform: 'translateY(-4px)'
                }
              }}>
                <NetworkCheckIcon sx={{ color: '#2196F3', fontSize: { xs: 32, sm: 40 }, mb: 1 }} />
                <Typography variant="h3" sx={{ color: '#2196F3', fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {statistics.online_printers || 0}
                </Typography>
                <Typography sx={{ color: '#B3B3B3', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'طابعات متصلة' : 'Online Printers'}
                </Typography>
              </Paper>
            </Fade>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Fade in timeout={1100}>
              <Paper sx={{ 
                p: { xs: 2, sm: 3 }, 
                backgroundColor: '#0D0D0D', 
                border: '1px solid #2D2D2D', 
                borderRadius: '16px',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#FFD700',
                  transform: 'translateY(-4px)'
                }
              }}>
                <SpeedIcon sx={{ color: '#FF9800', fontSize: { xs: 32, sm: 40 }, mb: 1 }} />
                <Typography variant="h3" sx={{ color: '#FF9800', fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {statistics.total_prints_today || 0}
                </Typography>
                <Typography sx={{ color: '#B3B3B3', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'طباعة اليوم' : "Today's Prints"}
                </Typography>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      )}

      {/* Printers List - Mobile Card View / Desktop Table View */}
      {isMobile ? (
        // Mobile Card View
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : paginatedPrinters.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
              <PrintIcon sx={{ fontSize: 48, color: '#B3B3B3', mb: 2 }} />
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا توجد طابعات' : 'No printers found'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2, backgroundColor: '#FFD700', color: '#000000' }}
              >
                {language === 'ar' ? 'إضافة طابعة' : 'Add Printer'}
              </Button>
            </Paper>
          ) : (
            paginatedPrinters.map((printer) => (
              <PrinterCard key={printer.id} printer={printer} />
            ))
          )}
          
          {!loading && paginatedPrinters.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={printers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ color: '#B3B3B3', mt: 2 }}
            />
          )}
        </Box>
      ) : (
        // Desktop Table View
        <Paper sx={{ backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الاسم' : 'Name'}</TableCell>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'النوع' : 'Type'}</TableCell>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>IP Address</TableCell>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>Port</TableCell>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الموقع' : 'Location'}</TableCell>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'إجراءات' : 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress sx={{ color: '#FFD700' }} />
                    </TableCell>
                  </TableRow>
                ) : paginatedPrinters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#B3B3B3' }}>
                      {language === 'ar' ? 'لا توجد طابعات' : 'No printers found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPrinters.map((printer) => (
                    <TableRow key={printer.id} sx={{ '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ComputerIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                          <Typography sx={{ color: '#FFFFFF' }}>
                            {language === 'ar' ? printer.name_ar || printer.name : printer.name}
                          </Typography>
                          {printer.is_default && (
                            <Chip
                              label={language === 'ar' ? 'افتراضي' : 'Default'}
                              size="small"
                              sx={{ backgroundColor: '#4CAF50', color: '#FFFFFF', height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPrinterTypeLabel(printer.printer_type)}
                          size="small"
                          sx={{ backgroundColor: '#FFD700', color: '#000000' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#FFFFFF', fontFamily: 'monospace' }}>
                          {printer.ip_address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#FFFFFF' }}>{printer.port}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#B3B3B3' }}>{printer.location || '-'}</Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(printer.is_active)}</TableCell>
                      <TableCell>
                        <Tooltip title={language === 'ar' ? 'اختبار الطباعة' : 'Test Print'}>
                          <IconButton onClick={() => handleTestPrint(printer)} disabled={testPrinting} sx={{ color: '#FFD700' }}>
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={language === 'ar' ? 'تعديل' : 'Edit'}>
                          <IconButton onClick={() => handleOpenDialog(printer)} sx={{ color: '#FFD700' }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={language === 'ar' ? 'حذف' : 'Delete'}>
                          <IconButton onClick={() => handleDeleteClick(printer)} sx={{ color: '#FF3D00' }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={printers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ color: '#B3B3B3', borderTop: '1px solid #2D2D2D' }}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog - Responsive */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: isMobile ? 0 : '16px',
            m: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #2D2D2D' }}>
          {editingPrinter
            ? (language === 'ar' ? 'تعديل الطابعة' : 'Edit Printer')
            : (language === 'ar' ? 'إضافة طابعة جديدة' : 'Add New Printer')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={language === 'ar' ? 'اسم الطابعة (إنجليزي)' : 'Printer Name (English)'}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            sx={{ mb: 2 }}
            InputProps={{ sx: { color: '#FFFFFF', backgroundColor: '#1A1A1A' } }}
            InputLabelProps={{ sx: { color: '#B3B3B3' } }}
          />
          <TextField
            fullWidth
            label={language === 'ar' ? 'اسم الطابعة (عربي)' : 'Printer Name (Arabic)'}
            name="name_ar"
            value={formData.name_ar}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            InputProps={{ sx: { color: '#FFFFFF', backgroundColor: '#1A1A1A' } }}
            InputLabelProps={{ sx: { color: '#B3B3B3' } }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'نوع الطابعة' : 'Printer Type'}</InputLabel>
            <Select
              name="printer_type"
              value={formData.printer_type}
              onChange={handleInputChange}
              label={language === 'ar' ? 'نوع الطابعة' : 'Printer Type'}
              sx={{ color: '#FFFFFF', backgroundColor: '#1A1A1A' }}
            >
              {printerTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {language === 'ar' ? type.label_ar : type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="IP Address"
            name="ip_address"
            value={formData.ip_address}
            onChange={handleInputChange}
            placeholder="192.168.1.100"
            required
            sx={{ mb: 2 }}
            InputProps={{ sx: { color: '#FFFFFF', backgroundColor: '#1A1A1A' } }}
            InputLabelProps={{ sx: { color: '#B3B3B3' } }}
          />
          <TextField
            fullWidth
            label="Port"
            name="port"
            type="number"
            value={formData.port}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            InputProps={{ sx: { color: '#FFFFFF', backgroundColor: '#1A1A1A' } }}
            InputLabelProps={{ sx: { color: '#B3B3B3' } }}
          />
          <TextField
            fullWidth
            label={language === 'ar' ? 'الموقع' : 'Location'}
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder={language === 'ar' ? 'مثال: مطبخ رئيسي' : 'e.g., Main Kitchen'}
            sx={{ mb: 2 }}
            InputProps={{ sx: { color: '#FFFFFF', backgroundColor: '#1A1A1A' } }}
            InputLabelProps={{ sx: { color: '#B3B3B3' } }}
          />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                />
              }
              label={language === 'ar' ? 'نشط' : 'Active'}
              sx={{ color: '#FFFFFF' }}
            />
            <FormControlLabel
              control={
                <Switch
                  name="is_default"
                  checked={formData.is_default}
                  onChange={handleInputChange}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }}
                />
              }
              label={language === 'ar' ? 'طابعة افتراضية' : 'Default Printer'}
              sx={{ color: '#FFFFFF' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #2D2D2D', p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={handleCloseDialog} fullWidth={isMobile} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSavePrinter}
            variant="contained"
            fullWidth={isMobile}
            sx={{ backgroundColor: '#FFD700', color: '#000000', '&:hover': { backgroundColor: '#FFE55C' } }}
          >
            {language === 'ar' ? 'حفظ' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FF3D00',
            borderRadius: isMobile ? 0 : '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FF3D00' }}>
          {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#FFFFFF' }}>
            {language === 'ar'
              ? `هل أنت متأكد من حذف الطابعة "${printerToDelete?.name}"؟`
              : `Are you sure you want to delete printer "${printerToDelete?.name}"?`}
          </Typography>
          {printerToDelete?.products_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' }}>
              {language === 'ar'
                ? `تحذير: هذه الطابعة مستخدمة من قبل ${printerToDelete.products_count} منتج. حذفها قد يؤثر على طباعة هذه المنتجات.`
                : `Warning: This printer is used by ${printerToDelete.products_count} products. Deleting it may affect printing for these products.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} fullWidth={isMobile} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            fullWidth={isMobile}
            sx={{ backgroundColor: '#FF3D00', color: '#FFFFFF', '&:hover': { backgroundColor: '#D32F2F' } }}
          >
            {language === 'ar' ? 'حذف' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : '#FF3D00'}`,
            borderRadius: '12px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPrinters;